/**
 * Sentral konfigurasjon. Alle API-adresser og ytelsesgrenser settes HER.
 *
 * Prioritet: URL-parametre (?api=…&project=…) → Vite-miljøvariabler (.env*) → standardverdier.
 *
 * - `apiBase`: Worker-API-et. COPC-URL-er bygges som apiBase + pointcloud.url og hentes DIREKTE fra
 *   Worker (CORS er aktivert på pointcloud-endepunktet).
 * - `metadataBase`: hvor prosjekt-JSON hentes. Standard = apiBase. Prosjekt-endepunktet sender i dag ikke
 *   CORS-header, så i dev brukes Vite-proxyen (`VITE_METADATA_VIA_PROXY=1` → relativ sti). Se README.
 */

export interface ViewerConfig {
  apiBase: string;
  metadataBase: string;
  projectId: string;
  /** maks antall punkter som vises samtidig (alle punktskyer til sammen) */
  pointBudget: number;
  /** maks samtidige node-forespørsler */
  maxConcurrentRequests: number;
  /** maks samtidige header-forespørsler ved oppstart */
  maxConcurrentHeaders: number;
  /** antall Web Workers for henting/dekoding */
  workerCount: number;
  /** forfin node så lenge projisert punktavstand er større enn dette (px) */
  refinePixels: number;
  /** "all": alle punktskyer slått på ved start (begrenset av budsjett/LOD); "none": ingen */
  initialVisibility: 'all' | 'none';
  /** bakgrunnskart ved start */
  basemap: 'map' | 'ortho' | 'none';
  /** 3D eller 2D toppvisning ved start */
  viewMode: '3d' | '2d';
  /** Worker-konvensjon for vektorlag (se docs/WORKER_API.md) */
  layerUrlTemplate: string;
  /** Worker-konvensjon for bilder; thumbnail = samme + ?variant=thumb */
  imageUrlTemplate: string;
  /** credentials for lag/bilder (fremtidig innlogging via Worker): 'omit' | 'same-origin' | 'include' */
  apiCredentials: RequestCredentials;
  /** hjul/pinch zoomer mot punktet under markøren (3D); ?cursorzoom=0 slår av */
  cursorZoom: boolean;
  /** rotasjon (venstre dra) dreier rundt punktet under markøren (3D); ?pivot=0 slår av */
  cursorPivot: boolean;
}

/** Standard punktbudsjett ut fra enheten: mobil/berøring 1,5 mill., lite minne (≤ 4 GB) 2,5 mill., ellers 4 mill. */
export function deviceBudget(mobile: boolean, deviceMemoryGb?: number): number {
  if (mobile) return 1_500_000;
  if (deviceMemoryGb !== undefined && deviceMemoryGb > 0 && deviceMemoryGb <= 4) return 2_500_000;
  return 4_000_000;
}

export const DEFAULT_API_BASE = 'https://geokontroll-lidar-api.geokontroll.workers.dev';
export const DEFAULT_PROJECT_ID = 'hallingdal';

type Env = Record<string, string | boolean | undefined>;

export function trimBase(base: string): string {
  return base.trim().replace(/\/+$/, '');
}

export function loadConfig(env: Env = import.meta.env, search: string = globalThis.location?.search ?? ''): ViewerConfig {
  const q = new URLSearchParams(search);
  const apiBase = trimBase(q.get('api') ?? (env.VITE_API_BASE as string | undefined) ?? DEFAULT_API_BASE);
  const viaProxy = !q.get('api') && String(env.VITE_METADATA_VIA_PROXY ?? '') === '1';
  const hw = typeof navigator !== 'undefined' && navigator.hardwareConcurrency ? navigator.hardwareConcurrency : 4;
  const num = (k: string, d: number) => {
    const v = q.get(k) ?? (env[`VITE_${k.toUpperCase()}`] as string | undefined);
    const n = v === undefined ? NaN : Number(v);
    return Number.isFinite(n) && n > 0 ? n : d;
  };
  return {
    apiBase,
    metadataBase: q.get('metadata') ? trimBase(q.get('metadata')!) : viaProxy ? '' : apiBase,
    projectId: (q.get('project') ?? (env.VITE_PROJECT_ID as string | undefined) ?? DEFAULT_PROJECT_ID).trim(),
    pointBudget: num('budget', deviceBudget(typeof matchMedia !== 'undefined' && matchMedia('(max-width: 900px), (pointer: coarse)').matches, typeof navigator !== 'undefined' ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory : undefined)),
    maxConcurrentRequests: num('requests', 6),
    maxConcurrentHeaders: num('headers', 6),
    // LAZ-dekomprimering (laz-perf) er flaskehalsen (~1,2 mill. pkt/s per worker); opptil 6 workers = maks samtidige noder
    workerCount: Math.max(1, Math.min(6, num('workers', hw - 1))),
    refinePixels: num('refine', 2.0),
    initialVisibility: q.get('visible') === 'none' ? 'none' : 'all',
    basemap: (['map', 'ortho', 'none'] as const).find((m) => m === (q.get('basemap') ?? env.VITE_BASEMAP)) ?? 'map',
    viewMode: q.get('view') === '2d' ? '2d' : '3d',
    layerUrlTemplate: (env.VITE_LAYER_URL_TEMPLATE as string | undefined) ?? '{api}/api/projects/{project}/layers/{layer}',
    imageUrlTemplate: (env.VITE_IMAGE_URL_TEMPLATE as string | undefined) ?? '{api}/api/projects/{project}/images/{image}',
    apiCredentials: ((env.VITE_API_CREDENTIALS as string | undefined) ?? 'omit') as RequestCredentials,
    cursorZoom: q.get('cursorzoom') !== '0',
    cursorPivot: q.get('pivot') !== '0',
  };
}
